import { useLayoutEffect, useMemo, useState } from 'react';

import cs from 'classnames';
import Tippy from '@tippyjs/react';

import { ia } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';
import Input from 'components/shared/Input/Input';
import Loader from 'components/shared/Loader/Loader';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import NotFound from 'components/practice/Dashboard/components/NotFound';

import TagPill from '../TagPill/TagPill';

import { getUniqueTags } from './lib/getUniqueTags';

const TagsDropdownPanel = ({
  tags = [],
  selectedTags = [],
  actions = [],
  handleUpdateTags = () => {},
  panelRef,
  onScroll,
  scrollPositionRef,
  closePopover = () => {},
  tagsKind = null,
  savingTagIds = []
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredTags = useMemo(() => {
    const combinedTags = getUniqueTags(tags, selectedTags) || [];
    if (!searchTerm) return combinedTags;
    return combinedTags.filter((tag) =>
      tag?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [tags, selectedTags, searchTerm]);

  useLayoutEffect(() => {
    if (panelRef?.current) {
      panelRef.current.scrollTop = scrollPositionRef?.current;
    }
  });

  const handleSearch = (e) => setSearchTerm(e.target.value);

  const renderOptions = (tag, index) => {
    const isSelected = selectedTags?.some((selectedTag) => selectedTag?.id === tag?.id);
    const isSaving = savingTagIds?.includes(tag?.id);
    return (
      <div
        key={tag?.id ?? index}
        className={cs(
          'rounded-md px-2 py-[6px]',
          isSaving ? 'pointer-events-none opacity-60' : 'hover:bg-primary-50',
          isSelected && 'bg-primary-25'
        )}
        onClick={() => {
          if (isSaving) return;
          handleUpdateTags(tag);
        }}>
        <div className="flex justify-between rounded-md">
          <TagPill tag={tag} className="h-[24px] cursor-default overflow-hidden px-[6px]" />
          {isSaving ? (
            <Loader color="neutral" size={16} />
          ) : (
            isSelected && <Icon icon="check-linear" color="neutral" stroke />
          )}
        </div>
      </div>
    );
  };

  const handleActionClick = (action, event) => {
    if (action.onClick) {
      event.stopPropagation();
      action.onClick();
      closePopover();
    }
  };

  const renderActions = (action, index) => {
    if (action?.tippyText) {
      return (
        <Tippy content={action?.tippyText} className="tippy-dark" placement="bottom">
          <div
            key={index}
            className={cs(
              'rounded-md px-2 py-[6px]',
              action?.disabled
                ? 'cursor-not-allowed hover:bg-neutral-100'
                : 'cursor-pointer hover:bg-primary-50'
            )}
            onClick={!action?.disabled ? (e) => handleActionClick(action, e) : null}>
            <div className="flex items-center justify-start gap-3">
              <Icon
                className={cs(
                  'rounded border border-solid border-neutral-300 p-1',
                  action?.disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                )}
                icon={action?.icon}
                size={28}
                color="primary"
                shade={900}
              />
              <span className="text-sm">{action?.label}</span>
            </div>
          </div>
        </Tippy>
      );
    }
    return (
      <div
        key={index}
        className={cs(
          'rounded-md px-2 py-[6px]',
          action?.disabled
            ? 'cursor-not-allowed hover:bg-neutral-100'
            : 'cursor-pointer hover:bg-primary-50'
        )}
        onClick={!action?.disabled ? (e) => handleActionClick(action, e) : null}>
        <div className="flex items-center justify-start gap-3">
          <Icon
            className={cs(
              'rounded border border-solid border-neutral-300 p-1',
              action?.disabled ? 'cursor-not-allowed' : 'cursor-pointer'
            )}
            icon={action?.icon}
            size={28}
            color="primary"
            shade={900}
          />
          <span className="text-sm">{action?.label}</span>
        </div>
      </div>
    );
  };

  return (
    <div className={cs('flex max-h-[400px] w-[360px] flex-col rounded-md bg-white')}>
      <Input
        placeholder="Search"
        value={searchTerm}
        onChange={(e) => handleSearch(e)}
        icon="search-linear"
        inputWrapperClassName="border-none !outline-none"
      />
      <div
        className="flex flex-col gap-2 overflow-y-scroll border border-l-0 border-r-0 border-solid border-neutral-200 py-2"
        ref={panelRef}
        onScroll={onScroll}>
        {!ia(filteredTags) && !searchTerm ? (
          <>
            <NotFound
              icon="illustration-empty-cpt-codes"
              title={`No ${tagsKind} tags created yet`}
              description={`Create new ${tagsKind} tags by clicking the action button below`}
              className="!p-0"
              iconSize={60}
              titleClassName="text-md"
            />
          </>
        ) : !ia(filteredTags) && searchTerm ? (
          <>
            <p className="pl-4 text-xs">No results found</p>
            <NotFound icon="illustration-empty-cpt-codes" className="!px-10 !py-8" iconSize={80} />
          </>
        ) : (
          <>
            <p className="pl-4 text-xs">All tags</p>
            <div className="flex flex-col px-2">
              {filteredTags.map((option, index) => renderOptions(option, index))}
            </div>
          </>
        )}
      </div>
      <div className="flex flex-col px-2 py-[6px]">
        <p className="pl-2 text-xs">Actions</p>
        <div className="grid grid-cols-2 gap-2">
          {actions?.map((action, index) => renderActions(action, index))}
        </div>
      </div>
    </div>
  );
};

export default withErrorBoundary(TagsDropdownPanel);
